import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import secureStorage from '../services/secureStorage';
import { menuTabs } from '../../features/layout/screens/MainLayoutScreen/constants/mainLayout.constant';

export const MENU_ACTIVE_TAB_KEY = 'menu_active_tab';

interface MenuState {
  activeTab: string;
  homeRefreshKey: number;
  /**
   * Sub-tab active within the current main screen.
   * Set by DashboardScreen when the user switches between "Repairs" and
   * "Parts Inventory" tabs.  Null for screens that have no sub-tabs.
   * Automatically reset to null whenever the main tab (activeTab) changes.
   */
  activeSubTab: string | null;
  /**
   * When the user is inside a create-flow overlay this holds the flow name
   * ("New Work Order" | "New Repair"); null otherwise.
   * Set/cleared by MainLayoutScreen whenever showCreateWorkOrder /
   * showRepairFlowScreen state changes.
   */
  activeFlowScreen: string | null;
}

const initialState: MenuState = {
  activeTab: menuTabs.home,
  homeRefreshKey: 0,
  activeSubTab: null,
  activeFlowScreen: null,
};

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<string>) {
      // Only reset the sub-tab when the user actually switches to a different
      // main tab.  Dispatching setActiveTab with the same tab value (e.g.
      // Home → Home on focus) must NOT clear the sub-tab because DashboardScreen
      // has already set it and won't re-dispatch until dashboardActiveTab changes.
      if (action.payload !== state.activeTab) {
        state.activeSubTab = null;
      }
      state.activeTab = action.payload;
      // Persist to secureStorage on all platforms (encrypted)
      secureStorage.setItem(MENU_ACTIVE_TAB_KEY, action.payload).catch(_e => {
        /* storage errors are non-fatal */
      });
    },
    triggerHomeRefresh(state) {
      state.homeRefreshKey += 1;
    },
    /** Update the active sub-tab within the current screen (e.g. "Repairs" or "Parts Inventory" on the Home screen). */
    setActiveSubTab(state, action: PayloadAction<string | null>) {
      state.activeSubTab = action.payload;
    },
    /** Track which create-flow overlay is currently visible, or null if none. */
    setActiveFlowScreen(state, action: PayloadAction<string | null>) {
      state.activeFlowScreen = action.payload;
    },
  },
});

export const { setActiveTab, triggerHomeRefresh, setActiveSubTab, setActiveFlowScreen } = menuSlice.actions;
export default menuSlice.reducer;